import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { LoginFormContext } from "context/LoginFormContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table";
import MDBox from "components/MDBox";
import { Autocomplete, Input, Pagination, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import colors from "assets/theme/base/colors";
import { LuArrowUpDown, LuArrowDown, LuArrowUp } from "react-icons/lu";
import { SEARCH_INPUT_TYPES } from "helpers/constant";
const { info } = colors;

const CollapseTable = ({
  tableTitle,
  data,
  totalEntries,
  defaultFilter,
  setDefaultFilter,
  canFilter,
  defaultColumns,
  subrow,
}) => {
  const { isLoading, access } = useContext(LoginFormContext);
  const { imageBorder, collapseTable } = colors;
  const [expanded, setExpanded] = useState({});
  const [currentPage, setCurrentPage] = useState(defaultFilter.currentPage);

  const tableContainerStyle = {
    overflowX: "auto",
    color: `${imageBorder.borderColor}`,
    backgroundColor: "#fff",
    borderRadius: "15px",
    paddingLeft: "0px",
  };

  const tableStyle = {
    borderCollapse: "collapse",
    fontWeight: 600,
    width: "100%",
    margin: "20px 0",
    fontSize: "14px",
    textAlign: "left",
    color: `${imageBorder.borderColor}`,
  };

  const theadThStyle = {
    fontWeight: "700",
    padding: "14px 25px",
    fontSize: "12px",
    borderBottom: `0.5px solid ${collapseTable.borderColor}`,
  };

  const tbodyTrStyle = {
    borderBottom: `1px solid ${collapseTable.borderColor}`,
  };

  const tbodyTrEvenStyle = {
    borderBottom: `1px solid ${collapseTable.borderColor}`,
  };

  const tdStyle = {
    padding: "10px 20px",
  };

  const table = useReactTable({
    data: data,
    columns: defaultColumns,
    state: {
      expanded,
      sorting: defaultFilter.sortBy,
    },
    getSubRows: (row) => {
      for (const key of subrow) {
        if (row?.[key]) {
          return row[key];
        }
      }
    },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const calculateRange = () => {
    const { currentPage, itemsPerPage } = defaultFilter;
    const totalRecords = totalEntries;
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalRecords);
    return { start, end };
  };

  const { start, end } = calculateRange();

  const handleChangeItemPerPage = async (newValue) => {
    setDefaultFilter((value) => ({
      ...value,
      itemsPerPage: parseInt(newValue, 10),
    }));
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    setDefaultFilter((prev) => ({ ...prev, currentPage: newPage }));
  };

  const isCurrentColumnSorted = (columnAccessor) => {
    return defaultFilter.sortBy?.id === columnAccessor;
  };

  const getSortDirection = (columnAccessor) => {
    if (!isCurrentColumnSorted(columnAccessor)) return null;
    return defaultFilter.sortBy?.value;
  };

  return (
    !isLoading && (
      <div style={tableContainerStyle}>
        <MDBox style={{ display: "flex", alignItems: "center" }}>
          <h3
            style={{
              fontWeight: "700",
              paddingLeft: "15px",
              paddingTop: "25px",
              fontSize: "18px",
              lineHeight: "21px",
              color: `${collapseTable.title}`,
              marginBottom: "20px",
            }}
          >
            {tableTitle}
          </h3>
        </MDBox>
        <MDBox sx={{ display: "flex", alignItems: "center", paddingLeft: "15px" }}>
          <MDBox display="flex" alignItems="center">
            <Autocomplete
              disableClearable
              value={defaultFilter?.itemsPerPage.toString()}
              options={["5", "10", "15", "20", "25", "50", "100"]}
              onChange={(event, newValue) => {
                handleChangeItemPerPage(newValue);
              }}
              size="small"
              sx={{ width: "4rem" }}
              renderInput={(params) => <MDInput {...params} />}
            />
            <MDTypography
              variant="caption"
              color="secondary"
              sx={{ textWrap: "nowrap", fontSize: "14px", fontWeight: "400", color: "#344767" }}
            >
              &nbsp;&nbsp;Entries per page
            </MDTypography>
          </MDBox>
        </MDBox>
        <table style={tableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnAccessor = header?.column?.columnDef?.filterAccessor;
                  const isSortable = header?.column?.columnDef?.sort;
                  const isActiveSortColumn = isCurrentColumnSorted(columnAccessor);
                  const sortDirection = getSortDirection(columnAccessor);

                  return (
                    <th key={header.id} colSpan={header.colSpan} style={theadThStyle}>
                      <>
                        <MDBox
                          sx={{
                            color: `${imageBorder.borderColor}`,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <MDBox
                            sx={{ color: `${imageBorder.borderColor}`, textTransform: "uppercase" }}
                          >
                            {header?.column?.columnDef?.header}
                          </MDBox>
                          {isSortable && (
                            <>
                              {isActiveSortColumn && sortDirection === "asc" && (
                                <LuArrowUp
                                  onClick={() =>
                                    setDefaultFilter((prev) => ({
                                      ...prev,
                                      sortBy: {
                                        id: columnAccessor,
                                        value: "desc",
                                      },
                                    }))
                                  }
                                  style={{ cursor: "pointer", marginLeft: "5px" }}
                                />
                              )}

                              {isActiveSortColumn && sortDirection === "desc" && (
                                <LuArrowDown
                                  onClick={() =>
                                    setDefaultFilter((prev) => ({
                                      ...prev,
                                      sortBy: {
                                        id: columnAccessor,
                                        value: "",
                                      },
                                    }))
                                  }
                                  style={{ cursor: "pointer", marginLeft: "5px" }}
                                />
                              )}

                              {!isActiveSortColumn && (
                                <LuArrowUpDown
                                  onClick={() =>
                                    setDefaultFilter((prev) => ({
                                      ...prev,
                                      sortBy: {
                                        id: columnAccessor,
                                        value: "asc",
                                      },
                                    }))
                                  }
                                  style={{ cursor: "pointer", marginLeft: "5px" }}
                                />
                              )}

                              {isActiveSortColumn && sortDirection === "" && (
                                <LuArrowUpDown
                                  onClick={() =>
                                    setDefaultFilter((prev) => ({
                                      ...prev,
                                      sortBy: {
                                        id: columnAccessor,
                                        value: "asc",
                                      },
                                    }))
                                  }
                                  style={{ cursor: "pointer", marginLeft: "5px" }}
                                />
                              )}
                            </>
                          )}
                        </MDBox>
                      </>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {canFilter && (
              <tr>
                {defaultColumns.map((item) => {
                  if (item?.filterInputType === SEARCH_INPUT_TYPES.TEXT) {
                    return (
                      <DataTableBodyCell key={item.id}>
                        <MDBox display="flex" alignItems="center" sx={{ maxWidth: "160px" }}>
                          <Input
                            style={{ fontSize: "12px", padding: "4px" }}
                            type="text"
                            value={defaultFilter?.filters?.[item?.accessor] || ""}
                            onChange={(e) => {
                              setDefaultFilter((prev) => ({
                                ...prev,
                                currentPage: 1,
                                filters: {
                                  ...prev.filters,
                                  [item?.accessor]: e.target.value,
                                },
                              }));
                            }}
                            variant="standard"
                            placeholder={item?.filterLabel}
                          />
                        </MDBox>
                      </DataTableBodyCell>
                    );
                  } else if (item?.filterInputType === SEARCH_INPUT_TYPES.AUTO_COMPLETE) {
                    return (
                      <DataTableBodyCell key={item.id}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          sx={{ minWidth: "150px", marginTop: "-5px", marginBottom: "8px" }}
                        >
                          <Autocomplete
                            options={item?.options}
                            value={
                              item?.options?.find(
                                (opt) => opt?.value === defaultFilter?.filters?.[item?.accessor]
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              setDefaultFilter((prev) => ({
                                ...prev,
                                currentPage: 1,
                                filters: {
                                  ...prev.filters,
                                  [item?.accessor]: newValue?.value,
                                },
                              }));
                            }}
                            size="small"
                            sx={{
                              width: "100%",
                              color: "#BFC3DA",
                              padding: "0px",
                              border: "0",
                            }}
                            popupIcon={
                              <KeyboardArrowDownIcon sx={{ fontSize: "14px", width: "18px" }} />
                            }
                            renderInput={(params) => (
                              <MDInput
                                {...params}
                                label={item?.filterLabel}
                                variant="standard"
                                sx={{ fontWeight: "400" }}
                              />
                            )}
                          />
                        </MDBox>
                      </DataTableBodyCell>
                    );
                  } else {
                    return <DataTableBodyCell key={item.id} />;
                  }
                })}
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} style={row.index % 2 === 0 ? tbodyTrEvenStyle : tbodyTrStyle}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} style={tdStyle}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <MDTypography
            variant="button"
            color="secondary"
            fontWeight="regular"
            style={{
              width: "100%",
              display: "block",
              textAlign: "center",
              paddingTop: "24px",
              paddingBottom: "24px",
              fontStyle: "italic",
            }}
          >
            No records found
          </MDTypography>
        )}
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{
            marginBottom: "20px",
            paddingTop: "10px",
            paddingLeft: "15px",
            paddingRight: "15px",
          }}
        >
          <MDBox mb={{ xs: 3, sm: 0 }} paddingTop={`${data?.length === 0 && "24px"}`}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              {totalEntries > 0
                ? `Showing ${start} to ${end} of ${totalEntries} entries`
                : `Showing ${start} to ${defaultFilter?.itemsPerPage} of ${totalEntries} entries`}
            </MDTypography>
          </MDBox>
          {totalEntries > defaultFilter?.itemsPerPage && (
            <Pagination
              count={Math.ceil(totalEntries / defaultFilter.itemsPerPage)}
              color="info"
              page={currentPage}
              onChange={handlePageChange}
              hidePrevButton={defaultFilter.currentPage === 1}
              hideNextButton={
                defaultFilter.currentPage === Math.ceil(totalEntries / defaultFilter.itemsPerPage)
              }
              sx={{
                position: "absolute",
                right: "40px",
                "& .MuiPaginationItem-root": {
                  height: "36px",
                  width: "36px",
                  fontSize: "16px",
                  borderRadius: "50%",
                  border: "1px solid #7b809a",
                  "&.Mui-selected": {
                    background: `${info.main} !important`,
                    color: "#FFFFFF",
                    cursor: "default",
                  },
                },
              }}
            />
          )}
        </MDBox>
      </div>
    )
  );
};

CollapseTable.propTypes = {
  tableTitle: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  totalEntries: PropTypes.number.isRequired,
  defaultFilter: PropTypes.object.isRequired,
  setDefaultFilter: PropTypes.func.isRequired,
  canFilter: PropTypes.bool,
  defaultColumns: PropTypes.array.isRequired,
  subrow: PropTypes.array,
};

export default CollapseTable;
